import React, { forwardRef, useMemo } from 'react'

import type { DropdownMenuSubTriggerProps } from '@radix-ui/react-dropdown-menu'

import { IconNameVariantType } from 'ui/components/Icon/Icon'
import { Body } from 'ui/components/Text'
import { StandardComponentProps } from 'ui/helpers/styles'

import { LeftSlot, MenuIcon, RightSlot, SubTrigger } from './Dropdown.parts'
import { DropdownEndIndicatorSubmenu } from './DropdownEndIndicatorSubmenu'

type IconProps = IconNameVariantType

type DropdownSubTriggerRef = HTMLDivElement

type DropdownSubTriggerProps = Omit<DropdownMenuSubTriggerProps, 'asChild'> &
    StandardComponentProps & {
        startIcon?: IconProps
        label?: React.ReactNode
        variant?: 'default' | 'destructive' | 'action'
        rightSlotContent?: React.ReactNode
    }

export const DropdownSubTrigger = forwardRef<DropdownSubTriggerRef, DropdownSubTriggerProps>(
    ({ startIcon, label, variant, rightSlotContent, ...props }, ref) => {
        const iconColor: React.ComponentPropsWithoutRef<typeof MenuIcon>['color'] = useMemo(() => {
            if (props.disabled) return undefined

            switch (variant) {
                case 'destructive':
                    return 'iconError'
                case 'action':
                    return 'iconTheme'
                default:
                    return undefined
            }
        }, [props.disabled, variant])

        const labelColor: React.ComponentPropsWithoutRef<typeof Body>['color'] = useMemo(() => {
            if (props.disabled) return undefined

            switch (variant) {
                case 'destructive':
                    return 'textError'
                case 'action':
                    return 'textTheme'
                default:
                    return undefined
            }
        }, [props.disabled, variant])

        return (
            <SubTrigger {...props} ref={ref}>
                {startIcon && (
                    <LeftSlot>
                        <MenuIcon size="m" color={iconColor} {...startIcon} />
                    </LeftSlot>
                )}
                <Body
                    size="m"
                    weight="regular"
                    color={labelColor}
                    style={{ lineHeight: 'inherit', fontStyle: 'inherit' }}
                >
                    {label}
                </Body>
                <RightSlot>
                    {rightSlotContent ? rightSlotContent : <DropdownEndIndicatorSubmenu />}
                </RightSlot>
            </SubTrigger>
        )
    }
)
